import { Link } from '@tanstack/react-router';
import { PlayIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiError } from '@/lib/api';
import { useAudits, useCreateAudit, useSpecs } from '@/lib/hooks/useAudits';
import { cn } from '@/lib/utils';

export function AuditsPage() {
  const { data: audits, isLoading } = useAudits();
  const { data: specs } = useSpecs();
  const create = useCreateAudit();
  const hasSpec = (specs?.items.length ?? 0) > 0;

  const onRun = async () => {
    try {
      const audit = await create.mutateAsync({});
      window.location.href = `/app/audits/${audit.id}`;
    } catch (e) {
      if (e instanceof ApiError) {
        const d = e.detail as { message?: string; error?: string } | undefined;
        alert(d?.message ?? d?.error ?? `Error ${e.status}`);
      }
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-8 py-10">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Audits</h1>
          <p className="mt-1 text-sm text-slate-500">
            History of every audit run on this org's API surface.
          </p>
        </div>
        <Button onClick={onRun} disabled={!hasSpec || create.isPending}>
          <PlayIcon className="h-4 w-4" />
          {create.isPending ? 'Starting…' : 'Run audit'}
        </Button>
      </header>

      {!hasSpec && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Add a spec first</CardTitle>
            <CardDescription>
              <Link to="/app/specs" className="underline">Add an OpenAPI spec</Link> before running an audit.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      <div className="mt-6 overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900">
            <tr>
              <th className="px-4 py-3 font-medium">Audit</th>
              <th className="px-4 py-3 font-medium">Trigger</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Score</th>
              <th className="px-4 py-3 font-medium">Started</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
            {isLoading && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">Loading…</td></tr>
            )}
            {audits && audits.items.length === 0 && !isLoading && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-slate-500">
                  No audits yet. Click "Run audit" above to start your first.
                </td>
              </tr>
            )}
            {audits?.items.map((a) => {
              const color =
                a.overall_score == null ? 'text-slate-400'
                : a.overall_score <= 40 ? 'text-[var(--color-critical)]'
                : a.overall_score <= 70 ? 'text-[var(--color-warning)]'
                : 'text-[var(--color-success)]';
              return (
                <tr key={a.id}>
                  <td className="px-4 py-3">
                    <Link to="/app/audits/$auditId" params={{ auditId: a.id }} className="font-mono text-xs hover:underline">
                      {a.id.slice(0, 8)}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-slate-500 capitalize">{a.trigger.replace('_', ' ')}</td>
                  <td className="px-4 py-3">
                    <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs capitalize',
                      a.status === 'complete' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200'
                      : a.status === 'failed' ? 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200'
                      : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400'
                    )}>
                      {a.status === 'queued' || a.status === 'running' ? (
                        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-slate-500" />
                      ) : null}
                      {a.status}
                    </span>
                  </td>
                  <td className={cn('px-4 py-3 font-mono', color)}>
                    {a.overall_score != null ? `${a.overall_score}/100` : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {a.started_at ? new Date(a.started_at).toLocaleString() : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
